import { Injectable } from '@angular/core'
import { SubscriptionService } from './subscription.service'
import { FirestoreStructure, Image, merchantParkingImagesPath, merchantParkingsPath, Parking, parkingsPath, ParkingStatus, StorageStructure } from '@parkupp/core'
import { addDoc, collection, collectionData, doc, docData, Firestore, query, serverTimestamp, updateDoc, where, Timestamp, Query, DocumentData } from '@angular/fire/firestore'
import * as firestore from 'firebase/firestore'
import { getDownloadURL, ref, Storage, uploadBytes } from '@angular/fire/storage'
import { firstValueFrom, Observable } from 'rxjs'
import { EnterpriseContextService } from './enterprise-context.service'

@Injectable({
    providedIn: 'root',
})
export class ParkingService {
    collection = collection(this.firestore, FirestoreStructure.PARKINGS)

    constructor(
        public firestore: Firestore, 
        private subscriptionSvc: SubscriptionService, 
        private storage: Storage,
        private enterpriseContext: EnterpriseContextService
    ) {}

    getDocRef(parkingKey: string) {
        return doc(this.firestore, `${FirestoreStructure.PARKINGS}/${parkingKey}`)
    }

    get(parkingKey: string): Observable<any> {
        const parkingDocRef = doc(this.firestore, `${FirestoreStructure.PARKINGS}/${parkingKey}`)
        return docData(parkingDocRef, { idField: '$key' })
    }

    async getPromise(parkingKey: string): Promise<Parking> {
        const parkingDocRef = doc(this.firestore, `${FirestoreStructure.PARKINGS}/${parkingKey}`)
        const documentData = await firstValueFrom(docData(parkingDocRef, { idField: '$key' }))
        return new Parking(documentData)
    }

    getStatic(parkingKey: string) {
        return new Promise<any>((resolve, reject) => {
            docData(doc(this.firestore, parkingsPath(), parkingKey)).subscribe((documentData: any) => {
                let parking = new Parking(documentData)
                parking.$key = parkingKey
                resolve(parking)
            })
        })
    }

    list(filters?: { status?: ParkingStatus; enterpriseKey?: string | null }): Observable<Parking[]> {
        let parkingsQuery: Query<DocumentData> = query(this.collection)
        if (filters?.status) {
            parkingsQuery = query(parkingsQuery, where('parkingStatus', '==', filters.status))
        }
        
        // Apply enterprise filter if specified or from context
        const filterKey = filters?.enterpriseKey ?? this.enterpriseContext.currentEnterpriseKey
        if (filterKey) {
            parkingsQuery = query(parkingsQuery, where('denormEnterpriseKey', '==', filterKey))
        }
        
        return collectionData(parkingsQuery, { idField: '$key' }) as Observable<Parking[]>
    }
    
    /**
     * List all parkings without enterprise filter
     */
    listAll(filters?: { status?: ParkingStatus }): Observable<Parking[]> {
        let parkingsQuery: Query<DocumentData> = query(this.collection)
        if (filters?.status) {
            parkingsQuery = query(parkingsQuery, where('parkingStatus', '==', filters.status))
        }
        return collectionData(parkingsQuery, { idField: '$key' }) as Observable<Parking[]>
    }

    listPromise(filters?: { status?: ParkingStatus }): Promise<Parking[]> {
        return new Promise<Parking[]>((resolve, reject) => {
            this.list(filters).subscribe((parkings) => {
                resolve(parkings)
            })
        })
    }

    listByMerchantRef(merchantRef: any): Observable<Parking[]> {
        const invoicesQuery = query(this.collection, where('merchantRef', '==', merchantRef))
        return collectionData(invoicesQuery, { idField: '$key' }) as Observable<Parking[]>
    }

    addMerchantParking(parking: Parking, uid: string) {
        const reference = collection(this.firestore, merchantParkingsPath(uid))
        parking.createdAt = serverTimestamp() as firestore.Timestamp
        parking.updatedAt = serverTimestamp() as firestore.Timestamp
        return addDoc(reference, parking.parse())
    }
    updateMerchantParking(fields: { [id: string]: any }, uid: string, docId: string): Promise<void> {
        fields['updatedAt'] = serverTimestamp() as firestore.Timestamp
        return updateDoc(doc(this.firestore, merchantParkingsPath(uid), docId), fields)
    }

    subscribeToParking(merchantKey: string, parkingKey: string) {
        return docData(doc(this.firestore, merchantParkingsPath(merchantKey), parkingKey))
    }

    update(merchantKey: string, parking: Parking) {
        if (!parking.$key) {
            throw new Error('Parking key is required')
        }
        return updateDoc(doc(this.firestore, merchantParkingsPath(merchantKey), parking.$key), parking.parse())
    }

    async updateFields(merchantKey: string, parkingKey: string, fields: { [id: string]: any }) {
        return await updateDoc(doc(this.firestore, merchantParkingsPath(merchantKey), parkingKey), fields)
    }

    deleteParkingImage(merchantKey: string, parkingKey: string, imageKey: string, images: { [id: string]: Image }) {
        delete images[`${imageKey}`]
        return updateDoc(doc(this.firestore, merchantParkingImagesPath(merchantKey, parkingKey)), { images: images })
    }
    uploadServiceAgreement(uid: string, parkingKey: string, serviceAgreement: any): Promise<string> {
        const fileExtension = serviceAgreement.name.split('.').pop()
        const filePath = `${StorageStructure.SERVICE_AGREEMENTS}/${uid}/${parkingKey}/${parkingKey}.${fileExtension}`
        return uploadBytes(ref(this.storage, filePath), serviceAgreement).then(() => {
            return getDownloadURL(ref(this.storage, filePath))
        })
    }
    uploadLeaseAgreement(uid: string, parkingKey: string, leaseAgreement: any): Promise<string> {
        const fileExtension = leaseAgreement.name.split('.').pop()
        const filePath = `${StorageStructure.LEASE_AGREEMENTS}/${uid}/${parkingKey}/${parkingKey}.${fileExtension}`
        return uploadBytes(ref(this.storage, filePath), leaseAgreement).then(() => {
            return getDownloadURL(ref(this.storage, filePath))
        })
    }

    getServiceAgreementUrl(uid: string, parkingKey: string, fileExtension: string): Promise<any> {
        return getDownloadURL(ref(this.storage, `${StorageStructure.SERVICE_AGREEMENTS}/${uid}/${parkingKey}/${parkingKey}.${fileExtension}`))
    }

    // Upload parking layout image directly
    uploadParkingLayoutImage(uid: string, parkingKey: string, parkingLayoutImage: any): Promise<string> {
        const fileExtension = parkingLayoutImage.name.split('.').pop()
        const filePath = `${StorageStructure.PARKING_LAYOUTS}/${uid}/${parkingKey}/${parkingKey}.${fileExtension}`

        return uploadBytes(ref(this.storage, filePath), parkingLayoutImage)
            .then(() => {
                return getDownloadURL(ref(this.storage, filePath))
            })
            .catch((error) => {
                console.error('Upload failed:', error)
                throw error
            })
    }

    // Get download URL for uploaded images
    getDownloadUrlFromPath(filePath: string): Promise<string> {
        return getDownloadURL(ref(this.storage, filePath))
    }

    // Update parking status (updates merchant subcollection which syncs to public via listener)
    async updateStatus(merchantKey: string, parkingKey: string, status: ParkingStatus): Promise<void> {
        await this.updateFields(merchantKey, parkingKey, {
            parkingStatus: status,
        })
    }

    // Delete parking (soft delete by setting status to DISABLED)
    async deleteParking(merchantKey: string, parkingKey: string): Promise<void> {
        await this.updateFields(merchantKey, parkingKey, {
            parkingStatus: ParkingStatus.DISABLED,
            deletedAt: serverTimestamp(),
        })
    }
}
